import { FC, useCallback, useEffect, useMemo } from 'react';
import * as Styled from './report-candidates-sidebar.styles';
import { AvatarItem, Icon, IconButton, Loader } from '@innowise-group/mui-kit';
import { useTranslation } from 'react-i18next';
import { ReportFormValues, TinyState } from '@modules';
import { format } from 'date-fns';
import {
  useAllStatusesOptions,
  useCompressedStateInQuery,
  useInfiniteScroll,
  useStatisticsAPI,
  useStatisticsCandidatesListAPI,
  useStatusesByVacancyStatisticsData,
} from '@innowise-group/core';
import { AppRoutes, DateFormats, RequestPreScreeningText } from '@constants';
import { useFormContext } from 'react-hook-form';

interface ReportCandidatesSidebarProps {
  openSidebar: {
    isOpen: boolean;
    statusesReport?: { statusId: string; vacancyId?: string };
    requestsReport?: { vacancyClaimId?: string; itemType?: string };
  };
  handleCloseSidebar: () => void;
}

const ReportCandidatesSidebar: FC<ReportCandidatesSidebarProps> = ({ openSidebar, handleCloseSidebar }) => {
  const { t } = useTranslation();
  const { isExportLoading } = useStatisticsAPI();
  const {
    isLoading,
    totalPages,
    items,
    clearCandidatesInStatusAndVacancy,
    fetchCandidatesInStatusAndVacancy,
    fetchCandidatesInRequestsHired,
    fetchCandidatesInRequests,
    exportCandidatesInReport,
    exportCandidatesInRequestsHired,
    exportCandidatesInRequests,
  } = useStatisticsCandidatesListAPI();
  const { statusOptions } = useAllStatusesOptions();
  const { watch } = useFormContext<ReportFormValues>();
  const dateMinForm = watch('dateMin');
  const dateMaxForm = watch('dateMax');

  const [state] = useCompressedStateInQuery<TinyState>();
  const { items: vacancies } = useStatusesByVacancyStatisticsData(true);
  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll(isLoading, totalPages);

  const handleClose = useCallback(() => {
    handleCloseSidebar();
    resetPageNum();
    clearCandidatesInStatusAndVacancy();
  }, []);

  const handleExport = useCallback(() => {
    if (!isExportLoading) {
      if (openSidebar.statusesReport?.statusId) {
        exportCandidatesInReport(state, openSidebar.statusesReport.statusId, openSidebar.statusesReport.vacancyId);
      } else {
        if (openSidebar.requestsReport?.itemType) {
          exportCandidatesInRequests(
            state,
            openSidebar.requestsReport.itemType,
            openSidebar.requestsReport.vacancyClaimId,
          );
        } else {
          exportCandidatesInRequestsHired(state, openSidebar.requestsReport?.vacancyClaimId);
        }
      }
    }
  }, [openSidebar, isExportLoading, state]);

  useEffect(() => {
    if (openSidebar.isOpen) {
      if (openSidebar.statusesReport?.statusId) {
        fetchCandidatesInStatusAndVacancy(
          state,
          pageNum,
          openSidebar.statusesReport.statusId,
          openSidebar.statusesReport.vacancyId,
        );
      } else {
        if (openSidebar.requestsReport?.itemType) {
          fetchCandidatesInRequests(
            state,
            pageNum,
            openSidebar.requestsReport.itemType,
            openSidebar.requestsReport.vacancyClaimId,
          );
        } else {
          fetchCandidatesInRequestsHired(state, pageNum, openSidebar.requestsReport?.vacancyClaimId);
        }
      }
    }
  }, [state, pageNum, openSidebar.isOpen]);

  const headerTitle = useMemo(() => {
    if (!openSidebar.isOpen) {
      return t('pages.candidates.pageName');
    }

    const dateMax = state?.dateMax || dateMaxForm;
    const dateMin = state?.dateMin || dateMinForm;
    const formatDateRange = `${format(new Date(dateMin), DateFormats.DayFirst)}-${format(
      new Date(dateMax),
      DateFormats.DayFirst,
    )}`;

    let reportType;
    let additionalInfo = '';

    if (openSidebar.statusesReport?.statusId) {
      const status = statusOptions.find(({ value }) => value === openSidebar.statusesReport.statusId);
      const vacancy = vacancies.find(
        ({ vacancy }) => vacancy.id?.toString() === openSidebar.statusesReport.vacancyId,
      )?.vacancy;

      reportType = t('pages.analytics.vacancyFunnel');
      additionalInfo = `${status?.title || ''} ${vacancy?.name || ''}`.trim();
    } else if (openSidebar.requestsReport?.itemType) {
      const { itemType, vacancyClaimId } = openSidebar.requestsReport;
      const screening = RequestPreScreeningText[itemType] || '';

      reportType = t('pages.analytics.requestsReport');
      additionalInfo = `${t(screening)} ${vacancyClaimId ? '№' + vacancyClaimId : ''}`.trim();
    } else {
      reportType = t('pages.analytics.requestsReport');
      additionalInfo = `${t('pages.analytics.hired')} ${
        openSidebar.requestsReport.vacancyClaimId ? '№' + openSidebar.requestsReport.vacancyClaimId : ''
      }`.trim();
    }

    return `${reportType} ${additionalInfo} ${formatDateRange}`;
  }, [t, openSidebar, vacancies, state, statusOptions, dateMinForm, dateMaxForm]);

  return (
    <Styled.Drawer anchor="right" open={openSidebar.isOpen} onClose={handleClose}>
      <Styled.DrawerHeader>
        <Styled.DrawerTitle>{headerTitle}</Styled.DrawerTitle>
        <IconButton onClick={handleExport} disabled={isExportLoading}>
          {isExportLoading ? <Loader size={22} /> : <Icon type="u_export" size={24} />}
        </IconButton>
        <IconButton onClick={handleClose}>
          <Icon type="u_multiply" size={24} />
        </IconButton>
      </Styled.DrawerHeader>
      <Styled.DrawerContent>
        {isLoading && pageNum === 1 ? (
          <Styled.LoaderContainer>
            <Loader />
          </Styled.LoaderContainer>
        ) : (
          <>
            {items?.length ? (
              items.map((item, index) => (
                <Styled.CandidateItem key={`${item.id}_${index}`} ref={lastElementRef}>
                  <AvatarItem photoId={item.photoId.toString()} />
                  <Styled.CandidateInfo>
                    <Styled.CandidateName to={`${AppRoutes.Candidates}/${item.id}`}>
                      {item.firstNameRu && item.firstNameEn
                        ? `${item.firstNameRu} ${item.lastNameRu} | ${item.firstNameEn} ${item.lastNameEn}`
                        : `${item.firstNameRu} ${item.lastNameRu} ${item.firstNameEn} ${item.lastNameEn}`}
                    </Styled.CandidateName>
                    <Styled.CandidateContent>
                      <Styled.CandidateIdChip>ID {item.id}</Styled.CandidateIdChip>
                      <Styled.CandidateProfession>
                        {item.candidateProfessions[0]?.profession?.translation || ''}
                      </Styled.CandidateProfession>
                    </Styled.CandidateContent>
                  </Styled.CandidateInfo>
                </Styled.CandidateItem>
              ))
            ) : (
              <Styled.LoaderContainer>{t('pages.candidates.candidateDetails.noData')}</Styled.LoaderContainer>
            )}
            {isLoading && pageNum !== 1 && (
              <Styled.LoaderContainer>
                <Loader />
              </Styled.LoaderContainer>
            )}
          </>
        )}
      </Styled.DrawerContent>
    </Styled.Drawer>
  );
};

export default ReportCandidatesSidebar;
